import cloud from "d3-cloud";
import { schemeCategory10 } from "d3-scale-chromatic";
import { select } from "d3-selection";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./KeywordCloudNoZoom.module.scss";

function getConceptLink(id) {
  if (id == null || id == undefined) {
    return "";
  }
  return "/explore/concept/" + id.toString();
}

const KeywordCloudNoZoom = ({
  keywords,
  selectedKeywords,
  width = 800,
  height = 300,
  padding = 5,
}) => {
  // const width = bigCloud ? 1200 : 800;
  // const height = bigCloud ? 600 : 300;
  // const padding = bigCloud ? 6.5 : 5;
  const navigate = useNavigate();
  const elementRef = useRef();
  const [data, setData] = useState([]);

  const normalizeValueToNewRange = (
    oldMin,
    oldMax,
    newMin,
    newMax,
    valueToNorm
  ) => {
    const oldRange = oldMax - oldMin;
    const newRange = newMax - newMin;

    const normalizedValue = Math.round(
      ((valueToNorm - oldMin) * newRange) / oldRange + newMin
    );

    if (normalizedValue >= newMin && normalizedValue <= newMax) {
      return normalizedValue;
    } else if (normalizedValue > newMax) {
      return newMax;
    } else if (normalizedValue < newMin) {
      return newMin;
    } else {
      return newMin;
    }
  };

  const preprocessInputData = (keywords) => {
    var minScoreValue = Math.min(...keywords.map((item) => item.score));
    var maxScoreValue = Math.max(...keywords.map((item) => item.score));

    // the keywords should be sorted based on their score
    var preprocessedData = keywords.map((item, index) => ({
      id: item.id,
      real_id: item.id,
      int_id: item.int_id,
      text: item.value,
      value: item.value,
      size: normalizeValueToNewRange(
        minScoreValue,
        maxScoreValue,
        15,
        40,
        item.score
      ),
      // "score": item.score,
      index: index,
      definition: item.definition,
    }));
    return preprocessedData;
  };

  useEffect(() => {
    var preprocessedData = preprocessInputData(keywords);

    setData([]);
    setData(preprocessedData);
  }, [keywords]);

  useEffect(() => {
    const el = elementRef.current;
    const svg = select(el);

    const layout = cloud()
      .size([width, height])
      .words(data)
      .padding(padding)
      .rotate(() => 0)
      .font("Roboto")
      .fontSize((d) => d.size)
      .random(() => 0.5)
      // .padding(6.5)
      .fontWeight("bold")
      .on("end", (words) => {
        const g = svg.selectAll("g").data([null]);

        // Enter new G element
        g.enter()
          .append("g")
          .attr("transform", `translate(${width / 2},${height / 2})`)
          .merge(g) // Merge enter and update selections
          .selectAll("text")
          .data(words, (d) => d.text)
          .join(
            (enter) =>
              enter
                .append("text")
                .attr("text-anchor", "middle")
                .attr(
                  "transform",
                  (d) => `translate(${d.x},${d.y}) rotate(${d.rotate})`
                )
                .style("font-size", (d) => `${d.size}px`)
                .style(
                  "fill",
                  (d, i, n) => schemeCategory10[n[i].__data__.int_id % 10]
                )
                .style("opacity", 0)
                .text((d) => d.text)
                .call((selection) =>
                  selection.transition().duration(500).style("opacity", 1)
                )
                .each(function (_, i, n) {
                  if (
                    selectedKeywords
                      .map((sk) => sk.value)
                      .includes(n[i].__data__.text)
                  ) {
                    // if (selectedKeywords.includes(n[i].__data__.text)) {
                    const bbox = _;

                    select(n[i].parentNode)
                      .select(
                        `rect.keyword-background-${n[i].__data__.real_id}`
                      )
                      .transition()
                      .duration(150)
                      .style("opacity", 0)
                      .remove();

                    select(n[i].parentNode)
                      .insert("rect", ":first-child")
                      .transition()
                      .delay(500)
                      .attr(
                        "class",
                        styles[`keyword-background-${n[i].__data__.real_id}`]
                      )
                      .style("fill", "lightgray")
                      .attr("rx", "6px") // Set horizontal border radius
                      // .attr("ry", "10%")
                      .attr("x", bbox.x - (bbox.width * 1.8) / 2)
                      .attr("y", bbox.y - (bbox.height * 1.8) / 2)
                      .attr("width", bbox.width * 1.8)
                      .attr("height", `${n[i].__data__.size * 1.5 * 1.8}px`)
                      .transition()
                      .duration(500)
                      .attr("x", bbox.x - bbox.width / 2)
                      .attr("y", bbox.y - bbox.height / 2)
                      .attr("width", bbox.width)
                      .attr("height", `${n[i].__data__.size * 1.5}px`);
                  }
                })
                .on("mouseover", (_, i, n) => {
                  const bbox = _;

                  select(n[i])
                    .style("cursor", "pointer")
                    .transition()
                    .duration(150)
                    .style("font-size", (d) => `${d.size * 1.5}px`);

                  select(n[i].parentNode)
                    .select(`rect.keyword-background-${n[i].__data__.real_id}`)
                    .transition()
                    .duration(150)
                    .attr("x", bbox.x - (bbox.width * 1.5) / 2)
                    .attr("y", bbox.y - (bbox.height * 1.5) / 2)
                    .attr("width", bbox.width * 1.5)
                    .attr("height", `${n[i].__data__.size * 1.5 * 1.5}px`);
                })
                .on("mouseout", (_, i, n) => {
                  // Handle mouseout event
                  // Example: Restore font size on mouseout

                  const bbox = _;

                  select(n[i])
                    .style("cursor", "default")
                    .transition()
                    .duration(150)
                    .style("font-size", (d) => `${d.size}px`);

                  select(n[i].parentNode)
                    .select(`rect.keyword-background-${n[i].__data__.real_id}`)
                    .transition()
                    .duration(150)
                    .attr("x", bbox.x - bbox.width / 2)
                    .attr("y", bbox.y - bbox.height / 2)
                    .attr("width", bbox.width)
                    .attr("height", `${n[i].__data__.size * 1.5}px`);
                })
                .on("contextmenu", (_, i, n) => {
                  // d3Event.preventDefault();
                })
                .on("click", (_, i, n) => {
                  const conceptLink = getConceptLink(n[i].__data__.real_id);
                  navigate(conceptLink);
                  window.location.reload();
                }),
            (update) =>
              update
                .transition()
                .duration(500)
                .attr(
                  "transform",
                  (d) => `translate(${d.x},${d.y}) rotate(${d.rotate})`
                )
                .style("font-size", (d) => `${d.size}px`)
                .style(
                  "fill",
                  (d, i, n) => schemeCategory10[n[i].__data__.int_id % 10]
                )
                .style("opacity", 1)
                .each(function (_, i, n) {
                  if (
                    selectedKeywords
                      .map((item) => item.value)
                      .includes(n[i].__data__.text)
                  ) {
                    // if (selectedKeywords.includes(n[i].__data__.text)) {
                    const bbox = _;

                    select(n[i].parentNode)
                      .select(
                        `rect.keyword-background-${n[i].__data__.real_id}`
                      )
                      .style("opacity", 0)
                      .remove();

                    select(n[i].parentNode)
                      .insert("rect", ":first-child")
                      .transition()
                      .delay(500)
                      .attr(
                        "class",
                        styles[`keyword-background-${n[i].__data__.real_id}`]
                      )
                      .style("fill", "lightgray")
                      .attr("rx", "6px") // Set horizontal border radius
                      // .attr("ry", "10%")
                      .attr("x", bbox.x - (bbox.width * 1.8) / 2)
                      .attr("y", bbox.y - (bbox.height * 1.8) / 2)
                      .attr("width", bbox.width * 1.8)
                      .attr("height", `${n[i].__data__.size * 1.5 * 1.8}px`)
                      .transition()
                      .duration(500)
                      .attr("x", bbox.x - bbox.width / 2)
                      .attr("y", bbox.y - bbox.height / 2)
                      .attr("width", bbox.width)
                      .attr("height", `${n[i].__data__.size * 1.5}px`);
                  }
                }),
            (exit) =>
              exit
                .each(function (_, i, n) {
                  select(n[i].parentNode)
                    .select(`rect.keyword-background-${n[i].__data__.real_id}`)
                    .style("opacity", 0)
                    .remove();
                })
                .transition()
                .duration(500)
                .style("opacity", 0)
                .remove()
          );
      });

    layout.start();
  }, [data]);

  return (
    <div className={styles["keyword-cloud-container"]}>
      <svg ref={elementRef} width={`${width}`} height={`${height}`} />
    </div>
  );
};

export default KeywordCloudNoZoom;
